import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FiUsers, FiTrash2, FiEye, FiSearch, FiFilter, FiUserPlus, FiMail, FiPhone, FiCheck, FiX } from 'react-icons/fi';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [businessTypes, setBusinessTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pages: 1, total: 0 });
  
  // Add User form state
  const [newUser, setNewUser] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    accountType: 'vendor',
    businessType: '',
    setupComplete: false,
    termsAccepted: false
  });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const queryParams = new URLSearchParams({
        page: pagination.current,
        limit: 20,
        search: searchTerm,
        role: selectedRole !== 'all' ? selectedRole : ''
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/management/users?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        setUsers(result.data.users || []);
        setPagination({
          current: result.data.page || 1,
          pages: result.data.pages || 1,
          total: result.data.total || 0
        });
      } else {
        throw new Error('Failed to load users');
      }
    } catch (err) {
      setError('Failed to load users');
      console.error('Users fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedRole, pagination.current]);

  useEffect(() => {
    fetchUsers();
    fetchBusinessTypes();
  }, [fetchUsers]);

  const fetchBusinessTypes = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/management/business-types`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        setBusinessTypes(result.data || []);
      }
    } catch (err) {
      console.error('Failed to load business types:', err);
    }
  };

  const handleDeleteUser = async (userId) => {
    const user = users.find(u => u._id === userId);
    setUserToDelete(user);
    setShowDeleteModal(true);
    setDeleteReason('');
  };

  const confirmDeleteUser = async () => {
    if (!deleteReason.trim()) {
      alert('Please provide a reason for deletion');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/management/users/${userToDelete._id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: deleteReason.trim() })
      });

      if (response.ok) {
        setUsers(users.filter(user => user._id !== userToDelete._id));
        alert('User deleted successfully and notification email sent');
        setShowDeleteModal(false);
        setUserToDelete(null);
        setDeleteReason('');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete user');
      }
    } catch (err) {
      alert('Failed to delete user: ' + err.message);
    }
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setShowViewModal(true);
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/management/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          firstName: newUser.firstName,
          lastName: newUser.lastName,
          email: newUser.email,
          password: newUser.password,
          accountType: newUser.accountType,
          businessType: newUser.businessType,
          setupComplete: newUser.setupComplete,
          termsAccepted: newUser.termsAccepted
        })
      });

      if (response.ok) {
        const result = await response.json();
        setUsers([result.data, ...users]);
        setShowAddModal(false);
        setNewUser({
          firstName: '',
          lastName: '',
          email: '',
          password: '',
          accountType: 'vendor',
          businessType: '',
          setupComplete: false,
          termsAccepted: false
        });
        alert('User added successfully');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add user');
      }
    } catch (err) {
      alert('Failed to add user: ' + err.message);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString();
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = searchTerm === '' || 
      user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = selectedRole === 'all' || user.role === selectedRole;
    
    return matchesSearch && matchesRole;
  });

  return (
    <Container>
      <PageHeader>
        <div>
          <PageTitle>
            <FiUsers /> User Management
          </PageTitle>
          <UserStats>
            <StatItem>
              <StatValue>{pagination.total}</StatValue>
              <StatLabel>Total Users</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue>{users.filter(u => u.isActive).length}</StatValue>
              <StatLabel>Active Users</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue>{users.filter(u => u.accountType === 'vendor').length}</StatValue>
              <StatLabel>Vendors</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue>{users.filter(u => u.accountType === 'freelancer').length}</StatValue>
              <StatLabel>Freelancers</StatLabel>
            </StatItem>
          </UserStats>
        </div>
        <AddUserButton 
          className="admin-btn admin-btn-primary"
          onClick={() => setShowAddModal(true)}
        >
          <FiUserPlus /> Add User
        </AddUserButton>
      </PageHeader>

      <FiltersCard>
        <FilterGroup>
          <SearchInput>
            <FiSearch />
            <input
              type="text"
              placeholder="Search users by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchInput>
          
          <RoleFilter>
            <FiFilter />
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
            >
              <option value="all">All Roles</option>
              <option value="user">Users</option>
              <option value="vendor">Vendors</option>
              <option value="admin">Admins</option>
            </select>
          </RoleFilter>
        </FilterGroup>
      </FiltersCard>

      <UsersCard>
        {loading ? (
          <LoadingContainer>
            <LoadingSpinner />
            <LoadingText>Loading users...</LoadingText>
          </LoadingContainer>
        ) : error ? (
          <ErrorMessage>{error}</ErrorMessage>
        ) : (
          <>
            <UsersTable>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Contact</th>
                  <th>Role</th>
                  <th>Activity</th>
                  <th>Status</th>
                  <th>Setup Complete</th>
                  <th>Terms & Conditions</th>
                  <th>Joined</th>
                  <th>Last Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user._id}>
                    <td>
                      <UserInfo>
                        <UserAvatar>
                          {(user.firstName && user.lastName) ? 
                            `${user.firstName.charAt(0)}${user.lastName.charAt(0)}` : 
                            user.email ? user.email.charAt(0).toUpperCase() : '?'
                          }
                        </UserAvatar>
                        <UserDetails>
                          <UserName>
                            {user.firstName || ''} {user.lastName || ''}
                          </UserName>
                          <UserEmail>{user.email || 'No email'}</UserEmail>
                        </UserDetails>
                      </UserInfo>
                    </td>
                    <td>
                      <ContactInfo>
                        <div><FiMail /> {user.email}</div>
                        {user.phone && <div><FiPhone /> {user.phone}</div>}
                      </ContactInfo>
                    </td>
                    <td>
                      <RoleBadge role={user.accountType || user.role}>
                        {user.accountType ? 
                          user.accountType.charAt(0).toUpperCase() + user.accountType.slice(1) : 
                          user.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 
                          'Unknown'
                        }
                      </RoleBadge>
                    </td>
                    <td>
                      <ActivityInfo>
                        {user.vendorProfile?.businessType?.activity || 
                         user.businessType?.activity ||
                         (user.accountType === 'vendor' ? 'Vendor (Type not specified)' : 'Not specified')}
                      </ActivityInfo>
                    </td>
                    <td>
                      <StatusBadge $status={user.isActive ? 'active' : 'inactive'}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </StatusBadge>
                    </td>
                    <td>
                      <SetupStatus $complete={user.setupComplete}>
                        {user.setupComplete ? <FiCheck /> : <FiX />}
                        {user.setupComplete ? 'Complete' : 'Incomplete'}
                      </SetupStatus>
                    </td>
                    <td>
                      <TermsStatus>
                        {user.termsAccepted ? (
                          <>
                            <FiCheck style={{ color: '#10b981', marginRight: '5px' }} />
                            Accepted
                          </>
                        ) : (
                          <>
                            <FiX style={{ color: '#ef4444', marginRight: '5px' }} />
                            Pending
                          </>
                        )}
                      </TermsStatus>
                    </td>
                    <td>{formatDate(user.createdAt)}</td>
                    <td>{user.lastLogin ? formatDate(user.lastLogin) : 'Never'}</td>
                    <td>
                      <ActionButtons>
                        <ActionButton 
                          onClick={() => handleViewUser(user)}
                          color="#3b82f6"
                          title="View User"
                        >
                          <FiEye />
                        </ActionButton>
                        <ActionButton 
                          onClick={() => handleDeleteUser(user._id)}
                          color="#ef4444"
                          title="Delete User"
                        >
                          <FiTrash2 />
                        </ActionButton>
                      </ActionButtons>
                    </td>
                  </tr>
                ))}
              </tbody>
            </UsersTable>

            {pagination.pages > 1 && (
              <PaginationContainer>
                <PaginationButton 
                  disabled={pagination.current === 1}
                  onClick={() => setPagination({...pagination, current: pagination.current - 1})}
                >
                  Previous
                </PaginationButton>
                <PaginationInfo>
                  Page {pagination.current} of {pagination.pages}
                </PaginationInfo>
                <PaginationButton 
                  disabled={pagination.current === pagination.pages}
                  onClick={() => setPagination({...pagination, current: pagination.current + 1})}
                >
                  Next
                </PaginationButton>
              </PaginationContainer>
            )}
          </>
        )}
      </UsersCard>

      {/* View User Modal */}
      {showViewModal && selectedUser && (
        <Modal onClick={() => setShowViewModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>User Details</ModalTitle>
              <CloseButton onClick={() => setShowViewModal(false)}>×</CloseButton>
            </ModalHeader>
            <ModalBody>
              <UserDetailGrid>
                <DetailItem>
                  <DetailLabel>Full Name</DetailLabel>
                  <DetailValue>
                    {selectedUser.firstName || ''} {selectedUser.lastName || ''}
                  </DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Primary Email</DetailLabel>
                  <DetailValue>{selectedUser.email || 'No email'}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Primary Phone</DetailLabel>
                  <DetailValue>
                    {selectedUser.vendorProfile?.contactInfo?.find(contact => contact.type === 'phone')?.number ||
                     selectedUser.profile?.phone || 
                     selectedUser.phone || 
                     'Not provided'}
                  </DetailValue>
                </DetailItem>
                {selectedUser.vendorProfile?.contactInfo && selectedUser.vendorProfile.contactInfo.filter(contact => contact.type === 'phone').length > 1 && (
                  <DetailItem>
                    <DetailLabel>Additional Phone Numbers</DetailLabel>
                    <DetailValue>
                      {selectedUser.vendorProfile.contactInfo
                        .filter(contact => contact.type === 'phone')
                        .slice(1) // Skip the first one (already shown as primary)
                        .map((contact, index) => (
                          <div key={index} style={{ marginBottom: '4px' }}>
                            {contact.number} {contact.name && `(${contact.name})`}
                          </div>
                        ))
                      }
                    </DetailValue>
                  </DetailItem>
                )}
                {selectedUser.vendorProfile?.businessName && (
                  <DetailItem>
                    <DetailLabel>Business Name</DetailLabel>
                    <DetailValue>{selectedUser.vendorProfile.businessName}</DetailValue>
                  </DetailItem>
                )}
                <DetailItem>
                  <DetailLabel>Business Type</DetailLabel>
                  <DetailValue>
                    {selectedUser.vendorProfile?.businessType?.activity || 
                     selectedUser.businessType?.activity || 
                     'Not specified'}
                  </DetailValue>
                </DetailItem>
                {selectedUser.vendorProfile?.contactInfo && selectedUser.vendorProfile.contactInfo.length > 0 && (
                  <DetailItem>
                    <DetailLabel>Landline Numbers</DetailLabel>
                    <DetailValue>
                      {selectedUser.vendorProfile.contactInfo
                        .filter(contact => contact.type === 'landline')
                        .map((contact, index) => (
                          <div key={index} style={{ marginBottom: '4px' }}>
                            {contact.number} {contact.name && `(${contact.name})`}
                          </div>
                        ))
                      }
                      {selectedUser.vendorProfile.contactInfo.filter(contact => contact.type === 'landline').length === 0 && (
                        <span style={{ color: '#666', fontStyle: 'italic' }}>No landline numbers</span>
                      )}
                    </DetailValue>
                  </DetailItem>
                )}
                <DetailItem>
                  <DetailLabel>Setup Complete</DetailLabel>
                  <DetailValue>
                    <StatusBadge $status={selectedUser.setupComplete ? 'completed' : 'pending'}>
                      {selectedUser.setupComplete ? 'Yes' : 'No'}
                    </StatusBadge>
                  </DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Terms Accepted</DetailLabel>
                  <DetailValue>
                    <StatusBadge $status={selectedUser.termsAccepted ? 'completed' : 'pending'}>
                      {selectedUser.termsAccepted ? 'Yes' : 'No'}
                    </StatusBadge>
                  </DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Account Created</DetailLabel>
                  <DetailValue>{formatDate(selectedUser.createdAt)}</DetailValue>
                </DetailItem>
              </UserDetailGrid>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}

      {/* Add User Modal */}
      {showAddModal && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <h2>Add New User</h2>
              <button 
                onClick={() => setShowAddModal(false)}
                style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}
              >
                ×
              </button>
            </ModalHeader>
            <ModalBody>
              <form onSubmit={handleAddUser}>
                <FormGroup>
                  <FormLabel>First Name</FormLabel>
                  <FormInput
                    type="text"
                    value={newUser.firstName}
                    onChange={(e) => setNewUser({...newUser, firstName: e.target.value})}
                    required
                  />
                </FormGroup>
                
                <FormGroup>
                  <FormLabel>Last Name</FormLabel>
                  <FormInput
                    type="text"
                    value={newUser.lastName}
                    onChange={(e) => setNewUser({...newUser, lastName: e.target.value})}
                    required
                  />
                </FormGroup>
                
                <FormGroup>
                  <FormLabel>Email</FormLabel>
                  <FormInput
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                    required
                  />
                </FormGroup>
                
                <FormGroup>
                  <FormLabel>Password</FormLabel>
                  <FormInput
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                    required
                    minLength="6"
                  />
                </FormGroup>
                
                <FormGroup>
                  <FormLabel>Role (Account Type)</FormLabel>
                  <FormSelect
                    value={newUser.accountType}
                    onChange={(e) => setNewUser({...newUser, accountType: e.target.value})}
                    required
                  >
                    <option value="vendor">Vendor</option>
                    <option value="freelancer">Freelancer</option>
                  </FormSelect>
                </FormGroup>
                
                <FormGroup>
                  <FormLabel>Field of Activity</FormLabel>
                  <FormSelect
                    value={newUser.businessType}
                    onChange={(e) => setNewUser({...newUser, businessType: e.target.value})}
                  >
                    <option value="">Select activity</option>
                    {businessTypes.map((type) => (
                      <option key={type._id} value={type._id}>
                        {type.activity}
                      </option>
                    ))}
                  </FormSelect>
                </FormGroup>
                
                <FormGroup>
                  <FormCheckboxGroup>
                    <FormCheckbox
                      type="checkbox"
                      checked={newUser.setupComplete}
                      onChange={(e) => setNewUser({...newUser, setupComplete: e.target.checked})}
                    />
                    <FormLabel>Mark setup as completed</FormLabel>
                  </FormCheckboxGroup>
                </FormGroup>
                
                <FormGroup>
                  <FormCheckboxGroup>
                    <FormCheckbox
                      type="checkbox"
                      checked={newUser.termsAccepted}
                      onChange={(e) => setNewUser({...newUser, termsAccepted: e.target.checked})}
                    />
                    <FormLabel>Terms & conditions accepted</FormLabel>
                  </FormCheckboxGroup>
                </FormGroup>
                
                <FormActions>
                  <button 
                    type="button" 
                    onClick={() => setShowAddModal(false)}
                    className="admin-btn admin-btn-secondary"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="admin-btn admin-btn-primary"
                  >
                    Add User
                  </button>
                </FormActions>
              </form>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}

      {/* Delete User Modal */}
      {showDeleteModal && userToDelete && (
        <Modal onClick={() => setShowDeleteModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Delete User Account</ModalTitle>
              <CloseButton onClick={() => setShowDeleteModal(false)}>×</CloseButton>
            </ModalHeader>
            <ModalBody>
              <div style={{ marginBottom: '20px' }}>
                <p style={{ color: '#d32f2f', fontWeight: 'bold', marginBottom: '10px' }}>
                  ⚠️ Warning: This action cannot be undone!
                </p>
                <p style={{ marginBottom: '15px' }}>
                  You are about to permanently delete the account for:
                </p>
                <div style={{ 
                  background: '#f5f5f5', 
                  padding: '15px', 
                  borderRadius: '8px', 
                  marginBottom: '20px' 
                }}>
                  <strong>{userToDelete.firstName} {userToDelete.lastName}</strong><br/>
                  <span style={{ color: '#666' }}>{userToDelete.email}</span>
                </div>
                <p style={{ marginBottom: '15px' }}>
                  The user will be notified via email about this deletion. Please provide a reason:
                </p>
                <textarea
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  placeholder="Enter the reason for deleting this user account..."
                  style={{
                    width: '100%',
                    minHeight: '100px',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                  required
                />
              </div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                gap: '12px',
                borderTop: '1px solid #e5e7eb',
                paddingTop: '20px'
              }}>
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(false)}
                  style={{
                    padding: '10px 20px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    background: 'white',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteUser}
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    borderRadius: '6px',
                    background: '#d32f2f',
                    color: 'white',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Delete Account
                </button>
              </div>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  padding: 2rem;
  min-height: 100vh;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const UserStats = styled.div`
  display: flex;
  gap: 2rem;

  @media (max-width: 768px) {
    justify-content: space-between;
  }
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: #666;
  margin-top: 0.25rem;
`;

const FiltersCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 1.5rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  margin-bottom: 1.5rem;
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchInput = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;

  svg {
    position: absolute;
    left: 1rem;
    color: #666;
    z-index: 1;
  }

  input {
    width: 100%;
    padding: 0.75rem 1rem 0.75rem 2.5rem;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 15px;
    background: rgba(255, 255, 255, 0.5);
    backdrop-filter: blur(5px);
    font-size: 1rem;
    outline: none;
    transition: all 0.3s ease;

    &:focus {
      border-color: #4f46e5;
      background: rgba(255, 255, 255, 0.8);
    }

    &::placeholder {
      color: #999;
    }
  }
`;

const RoleFilter = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  svg {
    position: absolute;
    left: 1rem;
    color: #666;
    z-index: 1;
    pointer-events: none;
  }

  select {
    padding: 0.75rem 1rem 0.75rem 2.5rem;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 15px;
    background: rgba(255, 255, 255, 0.5);
    backdrop-filter: blur(5px);
    font-size: 1rem;
    outline: none;
    cursor: pointer;
    min-width: 150px;

    &:focus {
      border-color: #4f46e5;
      background: rgba(255, 255, 255, 0.8);
    }
  }
`;

const UsersCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 1.5rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  gap: 1rem;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #333;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  color: #666;
  font-size: 1.1rem;
  margin: 0;
`;

const ErrorMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: #ef4444;
  font-size: 1.1rem;
`;

const UsersTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1rem;

  th {
    text-align: left;
    padding: 1rem;
    font-weight: 600;
    color: #1a1a1a;
    border-bottom: 2px solid rgba(0, 0, 0, 0.1);
    background: rgba(255, 255, 255, 0.05);
  }

  td {
    padding: 1rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    vertical-align: middle;
  }

  tr:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4f46e5, #7c3aed);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.9rem;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const UserName = styled.div`
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 0.2rem;
`;

const UserEmail = styled.div`
  font-size: 0.85rem;
  color: #666;
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  div {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;
    color: #666;

    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

const RoleBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  background: ${props => {
    switch (props.role) {
      case 'admin': return '#8b5cf6';
      case 'vendor': return '#f59e0b';
      case 'user': return '#3b82f6';
      default: return '#6b7280';
    }
  }};
  color: white;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  background: ${props => {
    switch (props.$status) {
      case 'active': return '#10b981';
      case 'completed': return '#10b981';
      case 'inactive': return '#ef4444';
      case 'pending': return '#f59e0b';
      default: return '#6b7280';
    }
  }};
  color: white;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button`
  width: 35px;
  height: 35px;
  border: none;
  border-radius: 8px;
  background: ${props => props.color}20;
  color: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.color};
    color: white;
    transform: translateY(-2px);
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
`;

const PaginationButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.5);
  color: #1a1a1a;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.8);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PaginationInfo = styled.span`
  color: #666;
  font-size: 0.9rem;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 0;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  background: rgba(255, 255, 255, 0.1);
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1.3rem;
  font-weight: 600;
  color: #1a1a1a;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.1);
    color: #1a1a1a;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  max-height: 60vh;
  overflow-y: auto;
`;

const UserDetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const DetailLabel = styled.span`
  font-weight: 600;
  color: #666;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DetailValue = styled.span`
  color: #1a1a1a;
  font-size: 1rem;
`;

const AddUserButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #2563eb;
    transform: translateY(-1px);
  }
`;

const ActivityInfo = styled.span`
  font-size: 0.9rem;
  color: #374151;
  padding: 4px 8px;
  background: #f3f4f6;
  border-radius: 4px;
`;

const SetupStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.9rem;
  color: ${props => props.$complete ? '#10b981' : '#ef4444'};
  font-weight: 500;
`;

const TermsStatus = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  font-weight: 500;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const FormCheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FormCheckbox = styled.input`
  width: 16px;
  height: 16px;
  accent-color: #3b82f6;
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
`;

export default Users;
